import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { blogPosts } from '../data/mock';
import { trackBlogView, trackSocialShare } from '../utils/analytics';

const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === parseInt(id));

  useEffect(() => {
    if (post) {
      trackBlogView(post.title, post.category);
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-6 py-3 rounded-full">
              <ArrowLeft className="mr-2" size={18} /> Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Find related posts from the same category
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/blog" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-8 transition-colors duration-300">
          <ArrowLeft size={20} className="mr-2" />
          Back to Blog
        </Link>

        {/* Article Header */}
        <div className="mb-12">
          <Badge className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white border-0 mb-4">
            {post.category}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center space-x-6 text-gray-400">
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <User size={18} />
              <span>{post.author}</span>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        <div className="mb-12 rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/30">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-96 object-cover"
          />
        </div>

        {/* Article Content */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-8 md:p-12 rounded-3xl mb-16">
          <div className="prose prose-invert prose-cyan max-w-none">
            <div className="text-gray-300 leading-relaxed text-lg whitespace-pre-line">
              {post.content}
            </div>
          </div>
        </Card>

        {/* Share Section */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-8 rounded-3xl mb-16">
          <h3 className="text-2xl font-bold text-white mb-6">Share This Article</h3>
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${window.location.href}`, '_blank')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-full"
            >
              Share on Twitter
            </Button>
            <Button
              onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`, '_blank')}
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 py-3 rounded-full"
            >
              Share on LinkedIn
            </Button>
            <Button
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`, '_blank')}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-full"
            >
              Share on WhatsApp
            </Button>
          </div>
        </Card>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div>
            <h3 className="text-3xl font-bold text-white mb-8">
              Related <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Articles</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.id}`}>
                  <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-2xl overflow-hidden hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-105 group cursor-pointer h-full">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                    </div>
                    <div className="p-6">
                      <Badge className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white border-0 mb-3">
                        {relatedPost.category}
                      </Badge>
                      <h4 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                        {relatedPost.title}
                      </h4>
                      <p className="text-gray-400 text-sm line-clamp-2">{relatedPost.excerpt}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <Card className="bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/50 p-12 rounded-3xl backdrop-blur-sm shadow-2xl shadow-cyan-500/30 text-center mt-16">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can implement these strategies for your business
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105">
              Get Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </Card>
      </div>
    </div>
  );
};

export default BlogPost;
